import React from 'react';
import { Card, Divider, message } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faServer,
  faPlug,
  faCube,
  faClock,
  faCircleCheck,
  faGlobe,
  faNetworkWired,
  faCopy,
} from '@fortawesome/free-solid-svg-icons';
import { plAppConfig } from '../config/plAppConfig';
import { Header } from 'antd/es/layout/layout';
import Title from 'antd/es/typography/Title';

// Reusable info line with copy
const InfoLine = ({
  icon,
  label,
  value,
}: {
  icon: any;
  label: string;
  value: string | number;
}) => {
  const copyToClipboard = async () => {
    await navigator.clipboard.writeText(`${value}`);
    message.success('Copied!');
  };

  return (
    <div className="flex items-center justify-between text-sm text-gray-700">
      <div className="flex items-center gap-2">
        <FontAwesomeIcon icon={icon} className="text-gray-500" />
        <strong>{label}:</strong> {value}
      </div>
      <FontAwesomeIcon
        icon={faCopy}
        className="text-gray-400 hover:text-black cursor-pointer"
        title={`Copy ${label}`}
        onClick={copyToClipboard}
      />
    </div>
  );
};

// Reusable URL line with copy
const UrlLine = ({ label, url }: { label: string; url: string }) => {
  const copyToClipboard = async () => {
    await navigator.clipboard.writeText(url);
    message.success('Copied!');
  };

  return (
    <div className="flex justify-between items-center text-sm">
      <div>
        <strong>{label}:</strong>{' '}
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 truncate"
        >
          {url}
        </a>
      </div>
      <FontAwesomeIcon
        icon={faCopy}
        className="text-gray-400 hover:text-black cursor-pointer ml-2"
        title={`Copy ${label}`}
        onClick={copyToClipboard}
      />
    </div>
  );
};

const PlApps = () => { 

  const getLocalAppUrl = (port: number) => `http://localhost:${port}`;
  const getLocalApiUrl = (port: number) => `http://localhost:${port}`;
  const getProdAppUrl = (key: string) => `https://${key}.parkinglap.com`;
  const getProdApiUrl = (key: string) => `https://${key}api.parkinglap.com`;

  const copyToClipboard = async (text: string) => {
    await navigator.clipboard.writeText(text);
    message.success('Copied!');
  };

  return (
    <div className="p-4">
      <Header className="bg-white px-6 sticky top-0 z-50 border-b border-gray-200">
        <Title level={3} className="m-0">ParkingLap Applications</Title>
      </Header>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 mt-4">
        {plAppConfig.map(app => {
          const localAppUrl = getLocalAppUrl(app.port);
          const localApiUrl = getLocalApiUrl(app.apiPort);
          const prodAppUrl = getProdAppUrl(app.key);
          const prodApiUrl = getProdApiUrl(app.key);

          return (
            <Card
              key={app.key}
              className="relative shadow-lg hover:shadow-xl transition-all hover:scale-[1.01] p-4"
              style={{ borderTop: `4px solid ${app.color}` }}
            >
              {/* Top-right full copy */}
              <FontAwesomeIcon
                icon={faCopy}
                title="Copy All Details"
                className="absolute top-2 right-2 text-gray-400 hover:text-black cursor-pointer"
                onClick={() =>
                  copyToClipboard(
                    `Name: ${app.name}
                    App Port: ${app.port}
                    API Port: ${app.apiPort}
                    Docker Name: ${app.key}
                    Started On: ${app?.startDate  || "Not Started Yet"}
                    Status: ${app?.status || "Not Started Yet"}
                    Local App URL: ${localAppUrl}
                    Local API URL: ${localApiUrl}
                    Production App URL: ${prodAppUrl}
                    Production API URL: ${prodApiUrl}`
                  )
                }
              />

              {/* Header */}
              <div className="flex items-center space-x-4 mb-3">
                <div
                  className="rounded-full w-12 h-12 flex items-center justify-center text-white"
                  style={{ backgroundColor: app.color }}
                >
                  <FontAwesomeIcon icon={app.icon} />
                </div>
                <h3 className="text-lg font-semibold">{app.name}</h3>
              </div>

              {/* Info Section */}
              <div className="space-y-2">
                <InfoLine icon={faServer} label="App Port" value={app.port} />
                <Divider className="my-2" />
                <InfoLine icon={faPlug} label="API Port" value={app.apiPort} />
                <Divider className="my-2" />
                <InfoLine icon={faCube} label="Docker Name" value={app.key} />
                <Divider className="my-2" />
                <InfoLine icon={faClock} label="Started On" value={app?.startDate || "Not Started Yet"} />
                <Divider className="my-2" />
                <InfoLine icon={faCircleCheck} label="Status" value={app?.status || "Not Started Yet"}  />
              </div>

              {/* Local URLs */}
              <div className="mt-4">
                <p className="font-semibold mb-1 flex items-center gap-2">
                  <FontAwesomeIcon icon={faNetworkWired} />
                  Local URLs:
                </p>
                <Divider className="my-2" />
                <UrlLine label="App" url={localAppUrl} />
                <Divider className="my-2" />
                <UrlLine label="API" url={localApiUrl} />
              </div>

              {/* Production URLs */}
              <div className="mt-4">
                <p className="font-semibold mb-1 flex items-center gap-2">
                  <FontAwesomeIcon icon={faGlobe} />
                  Production URLs:
                </p>
                <Divider className="my-2" />
                <UrlLine label="App" url={prodAppUrl} />
                <Divider className="my-2" />
                <UrlLine label="API" url={prodApiUrl} />
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default PlApps;
